<script>
	import {onMount} from 'svelte';
	import {noSelectorReturn} from '../../share/noSelectorReturn';
	import {apiUrl} from '../../share/stores';
	import {jumpByAnchor} from "../../share/utils";

	let div;

	export let id;

	function setMaxHeightForAnimation() {

		noSelectorReturn(div, '.accordion-body');

		if (!div) {
			return;
		}

		Array.from(div.querySelectorAll('.accordion-body')).forEach((accordionBody) => {
			accordionBody.style.height = accordionBody.scrollHeight + 'px';
		});

		requestAnimationFrame(setMaxHeightForAnimation);
	}

	function setTextByViewportSize() {

		noSelectorReturn(div, '.accordion-body');

		if (!div) {
			return;
		}

		const checkboxLabels = div.querySelectorAll('label');

		Array.from(checkboxLabels).forEach((label) => {
			const button = label.querySelector('button');
			if(!button) {
				return;
			}
			if (label.previousElementSibling.checked) {
				button.innerText = button.dataset.moreButtonTextClose;
				return;
			}
			button.innerText = button.dataset.moreButtonText ? button.dataset.moreButtonText : '';
			if(window.innerWidth <= 600 && button.dataset.moreButtonTextMobile) {
				button.innerText = button.dataset.moreButtonTextMobile;
			}
		});

		requestAnimationFrame(setTextByViewportSize);
	}

	function toggleExpansion() {
		let allItemsExpandable = div.querySelector('[all_items_expandable]');
		const checkboxLabels = div.querySelectorAll('label');

		Array.from(checkboxLabels).forEach((label) => {
			label.addEventListener('click', () => {
				const button = label.querySelector('button');
				let buttonText = button.innerText;
				if (button.dataset.moreButtonTextClose &&
						button.dataset.moreButtonText.toLowerCase() === button.innerText.toLowerCase()) {
					buttonText = button.dataset.moreButtonTextClose;
				}
				if (button.dataset.moreButtonText &&
						button.dataset.moreButtonText.toLowerCase() !== button.innerText.toLowerCase()) {
					buttonText = button.dataset.moreButtonText;
				}
				button.innerText = buttonText;
			});
		});

		if (allItemsExpandable && checkboxLabels) {
			allItemsExpandable = allItemsExpandable.getAttribute('all_items_expandable') === 'true';

			if (allItemsExpandable) {
				return;
			}

			// uncheck other Labels:
			Array.from(checkboxLabels).forEach((label) => {
				const expandable = label.parentElement.parentElement.classList.contains('expandable_true');
				if (!expandable) {
					label.addEventListener('click', () => {
						window.location = label.parentElement.href;
					});
					return;
				}
				label.addEventListener('click', () => {
					Array.from(checkboxLabels).forEach((otherLabel) => {
						if (otherLabel !== label) {
							otherLabel.previousElementSibling.checked = false;
						}
					});
				});
			});
		}
	}

	function fixPseudoSrcPaths() {

		noSelectorReturn(div, '.accordion-button');
		const labels = div.querySelectorAll('.accordion-label');

		if (!labels.length) {
			requestAnimationFrame(fixPseudoSrcPaths);
			return;
		}

		labels.forEach((label) => {

			if (typeof label.querySelector !== 'function') {
				return;
			}

			const button = label.querySelector('button');

			if (!button || !button.hasAttribute('style')) {
				return;
			}

			let style = button.getAttribute('style').replace('/url(/', `/url(${apiUrl}/`);
			button.setAttribute('style', style);
		});
	}

	onMount(() => {
		setMaxHeightForAnimation();
		setTextByViewportSize();
		toggleExpansion();
		fixPseudoSrcPaths();
		jumpByAnchor(div);
	});
</script>

<div id={id} bind:this={div} class="accordion marginbox">
	<slot/>
</div>

<style global lang="scss">
	@import "../../style/variables";
	@import "../../style/mixins";

	.accordion {
		--accordion-label-height: #{px-to-em(80)};
		width: 100%;
		margin: var(--spacing-mobile);

		h3 {
			margin-bottom: $spacing-mobile;
		}

		.accordion {
			&.black {
				background: black;
				color: white;
			}
		}

		.accordion-item {
			--border: .06em solid var(--color-burgeins-3);
			padding: calc(var(--spacing-mobile) * .2) 0;
			border-bottom: var(--border);

			label {
				cursor: pointer;
			}

			ul > li {
				display: block;
			}

			&:first-of-type {
				border-top: var(--border);
			}

			.accordion-body {
				display: flex;
				flex-wrap: nowrap;
				flex-direction: column;
				overflow: hidden;
				transition: height var(--animation-duration);

				ul {
					list-style-position: outside;
					margin-left: 1em;
				}
			}

			:global(.accordion-label) {
				cursor: pointer;
			}

			input {
				display: none;

				//change icons relative to opened/closed accordion-item:
				+ label button {
					position: relative;
				}

				+ label button[style*="closed_pseudo_src_before"]::before {
					content: "";
					-webkit-mask-image: var(--closed_pseudo_src_before);
					mask-image: var(--closed_pseudo_src_before);
				}

				&:checked + label button[style*="opened_pseudo_src_before"]::before {
					-webkit-mask-image: var(--opened_pseudo_src_before);
					mask-image: var(--opened_pseudo_src_before);
				}

				+ label button[style*="closed_pseudo_src_after"]::after {
					content: "";
					-webkit-mask-image: var(--closed_pseudo_src_after);
					mask-image: var(--closed_pseudo_src_after);
				}

				&:checked + label button[style*="opened_pseudo_src_after"]::after {
					-webkit-mask-image: var(--opened_pseudo_src_after);
					mask-image: var(--opened_pseudo_src_after);
				}

				+ label button.arrow::before {
					content: "";
					-webkit-mask-image: unset;
					mask-image: unset;
					transform: rotate(45deg);
					border: solid black;
					border-width: 0 3px 3px 0;
					padding: 5px;
					position: relative;
					background-color: unset;
					width: .1em;
					height: .1em;
					transform-origin: center;
					transition: transform .2s, border-color .2s;
					margin-top: -1em;

					@include for-tablet-landscape-up {
						border-width: 0 5px 5px 0;
						padding: 10px;
					}

					.useCustomCss & {
						border-color: #dedede;
					}
				}

				&:checked + label button.arrow::before {
					-webkit-mask-image: unset;
					mask-image: unset;
					transform: rotate(-135deg);
				}

				&:not(:checked) ~ .accordion-body {
					height: 0 !important;
				}
			}

			label {
				display: flex;
				flex-direction: row;
				height: var(--accordion-label-height);

				p {
					flex: auto 1 1;
					margin: auto 0;
					font-weight: 600;
				}

				a {
					margin: auto 0;

					@include for-desktop-up {
						font-size: 2rem;
						line-height: 4rem;
					}
				}

				button {
					pointer-events: none;
					flex: 30% 1 1;
					margin: auto 0;
					background-color: transparent;
					border: none;
					outline: none;
					padding: 0;
					text-transform: uppercase;
					font-weight: 300;
					justify-content: flex-end;
					display: flex;
					flex-flow: nowrap;

					color: $color-burgeins-6; // default color

					@include for-tablet-landscape-up {
						text-align: right;
					}
				}

				.button_before {
					padding-left: 1.8em;
				}

				.button_before::before {
					position: relative;
					aspect-ratio: 1;
				}

				.has_before_yes::before {
					content: "";
					margin-right: 0.5em;
				}

				.has_after_yes::after {
					content: "";
					margin-left: 0.5em;
				}

				.has_before_yes::before,
				.has_after_yes::after {
					width: 0.9em;
					height: 0.9em;
					top: .1em;
					position: relative;
					display: flex;
					align-self: center;
					background-color: currentColor;
					-webkit-mask-repeat: no-repeat;
					mask-repeat: no-repeat;
					-webkit-mask-size: 100% 100%;
					mask-size: 100% 100%;
				}

				.button_after::after {
					position: relative;
					aspect-ratio: 1;
				}
			}

			&.expandable_false label {
				height: auto;
				margin: $media-gap 0;
				cursor: unset;
			}

			&-content {
				width: 70%;
				margin: 0 0 var(--spacing-mobile) 0;

				@include for-tablet-landscape-up {
					padding: 0 var(--spacing-tablet) 0 0;
				}

				ul {
					margin-bottom: $spacing-mobile * .5;
				}

				h4 {
					margin-top: $spacing-mobile;
					margin-bottom: $spacing-mobile * .3;
				}
			}

			&-buttons {
				width: 100%;

				@include for-tablet-landscape-up {
					width: 30%;
				}

				&:last-of-type {
					margin-bottom: var(--spacing-mobile);
				}

				a {
					width: max-content;
					float: left;
					margin: 0 0 calc(var(--spacing-mobile) / 2) 0;
					display: block;
					padding: 1em 1em;
					text-align: center;
					text-transform: uppercase;
					border-radius: var(--border-burgeins);
					color: #FFF;
					background-color: var(--color-burgeins-6);
					text-decoration: none;
					@include font-px-to-rem(18, 20);
				}
			}

			&.special_border--black {
				--border-black: .06em solid black;
				border-bottom: var(--border-black);
			}

			&:first-of-type.special_border--black {
				border-top: var(--border-black);
			}
		}

		.flex {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 0.75em;

			.accrdion_headline_button {
				display: block;
				padding: 0.75em;
				text-align: center;
				text-transform: uppercase;
				border-radius: var(--border-burgeins);
				text-decoration: none;
				background-color: var(--color-burgeins-6);
				color: #ffffff;
			}
		}
	}
</style>
